"use client";

import { ModuleHeroShowcase } from "@lib/types";
import { useInView } from "motion/react";
import { useEffect, useRef, useState } from "react";

import { CtaFlexible } from "@/components/shared/cta";
import { useIsMobile } from "@/hooks/useMobile";
import { cn } from "@/lib/ui/cn";

type ShowcaseItemsProps = {
  items: ModuleHeroShowcase["items"];
};

export const ShowcaseItems = ({ items }: ShowcaseItemsProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const [activeMenuIndex, setActiveMenuIndex] = useState<number>(0);

  const navScrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef);
  const isProgrammaticScroll = useRef(false);
  const isMobile = useIsMobile();
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const pauseAutoRotation = () => {
    setIsHovering(true);

    // Clear existing timeout
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }

    // Resume auto-rotation after 5 seconds of inactivity
    inactivityTimeoutRef.current = setTimeout(() => {
      setIsHovering(false);
    }, 5000);
  };

  const handleNavScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (isProgrammaticScroll.current) return;

    // Pause auto-rotation when user manually scrolls
    pauseAutoRotation();

    const scrollContainer = e.currentTarget;
    const containerWidth = scrollContainer.offsetWidth;

    let newActiveMenu = 0;
    let maxVisibleArea = 0;

    // Calculate which item has the most visible area
    for (let i = 0; i < items.length; i++) {
      const item = scrollContainer.children[i];
      if (item) {
        const itemRect = item.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();

        const itemLeft = itemRect.left - containerRect.left;
        const itemRight = itemRect.right - containerRect.left;
        const visibleLeft = Math.max(0, itemLeft);
        const visibleRight = Math.min(containerWidth, itemRight);
        const visibleArea = Math.max(0, visibleRight - visibleLeft);

        if (visibleArea > maxVisibleArea) {
          maxVisibleArea = visibleArea;
          newActiveMenu = i;
        }
      }
    }

    if (newActiveMenu !== activeMenuIndex) {
      setActiveMenuIndex(newActiveMenu);
    }
  };

  const scrollToItem = (index: number) => {
    if (!navScrollRef.current) return;

    const activeItem = navScrollRef.current.children[index];
    if (activeItem) {
      isProgrammaticScroll.current = true;
      activeItem.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });

      setTimeout(() => {
        isProgrammaticScroll.current = false;
      }, 500);
    }
  };

  // Auto-rotation effect - only when component is in view
  useEffect(() => {
    if (isHovering) return;

    const interval = setInterval(() => {
      setActiveMenuIndex((prev) => {
        const newMenu = (prev + 1) % items.length;
        if (isInView && isMobile) {
          scrollToItem(newMenu);
        }
        return newMenu;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [isHovering, isInView, isMobile, items.length]);

  // Cleanup inactivity timeout on unmount
  useEffect(() => {
    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
    };
  }, []);

  const handleDotClick = (index: number) => {
    scrollToItem(index);
    setActiveMenuIndex(index);
  };

  return (
    <div className="flex flex-col" ref={containerRef}>
      <div
        className={cn(
          "gap-gap-element-md scrollbar-hide scrollbar flex min-h-[223px] snap-x snap-mandatory items-center overflow-x-auto",
          "tablet:min-h-[193px]",
          "desktop:max-w-[280px] desktop:flex-col desktop:gap-0 desktop:min-h-auto",
        )}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onTouchStart={pauseAutoRotation}
        ref={navScrollRef}
        onScroll={handleNavScroll}
      >
        {items.map((item, index) => {
          const isActive = activeMenuIndex === index;
          const isInactive = activeMenuIndex !== index;

          return (
            <div
              key={item.eyebrow}
              onMouseEnter={() => setActiveMenuIndex(index)}
              className={cn(
                "not-last:pb-gap-element-lg bg-neutral-darkest/15 group box-border flex max-h-[223px] min-w-[300px] cursor-default snap-center flex-col rounded-lg border-[3px] border-transparent backdrop-blur-md transition-all duration-500 ease-in-out",
                "max-desktop:last:mr-padding-global max-desktop:first:ml-padding-global",
                "tablet:min-w-[560px] tablet:max-h-[193px]",
                "desktop:not-last:hover:pb-gap-element-xl desktop:bg-none desktop:p-0 desktop:backdrop-blur-none desktop:border-none desktop:rounded-none desktop:bg-transparent desktop:min-w-auto desktop:max-h-[254px]",
                "p-padding-element-md",
                isActive &&
                  "desktop:not-last:pb-gap-element-xl desktop:bg-none border-[rgba(189,212,252,0.2)] bg-[linear-gradient(180deg,rgba(189,212,252,0.2)_0%,rgba(178,178,179,0.2)_100%)] bg-blend-soft-light",
                isInactive && "desktop:pointer-events-auto pointer-events-none",
              )}
            >
              <div
                className={cn(
                  "typography-heading-4 text-neutral opacity-50 transition-all duration-500 ease-in-out group-hover:text-white",
                  "desktop:opacity-100",
                  isActive && "desktop:pb-2.5 text-white opacity-100",
                )}
              >
                {item.eyebrow}
              </div>

              <div
                className={cn(
                  "flex h-full flex-col gap-2.5 overflow-hidden transition-all duration-500 ease-in-out group-hover:max-h-[300px]",
                  "desktop:max-h-0 desktop:opacity-0 desktop:group-hover:opacity-100",
                  isActive && "desktop:max-h-[300px] desktop:opacity-100",
                  isInactive && "max-desktop:text-neutral-light max-desktop:opacity-50",
                )}
              >
                <div
                  className={cn(
                    "typography-text-medium-medium",
                    isInactive && "max-desktop:text-neutral-light max-desktop:opacity-50",
                  )}
                >
                  {item.title}
                </div>
                <div
                  className={cn(
                    "typography-text-regular-normal text-neutral-light line-clamp-4",
                    "tablet:line-clamp-2",
                    "desktop:line-clamp-3",
                    isInactive && "max-desktop:text-neutral-light max-desktop:opacity-50",
                  )}
                >
                  {item.description}
                </div>
                {item.linkCta && (
                  <div className="mt-auto w-fit">
                    <CtaFlexible
                      cta={item.linkCta as Parameters<typeof CtaFlexible>[0]["cta"]}
                      className="typography-text-regular-normal"
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div
        className={cn("py-padding-element-lg gap-gap-element-2xs mx-auto flex", "desktop:hidden")}
      >
        {items.map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-1 w-[52px] cursor-pointer rounded-sm bg-white/60 transition-colors",
              activeMenuIndex === index && "bg-emerald-light",
            )}
            onClick={() => handleDotClick(index)}
          />
        ))}
      </div>
    </div>
  );
};
